export type PiiScrubRule = {
  /** Name of the field (schema property) that carries PII in one or more of its subkeys. */
  field: string
  /**
   * Subkeys to delete entirely from the field's schema — e.g. dropping `enumNames` so the
   * renderer falls back to the stable `enum` values. Deleting a missing subkey is a no-op.
   */
  remove?: string[]
  /** Subkeys to set to a fixed value — e.g. emptying `anyOf` to `[]`. */
  reset?: Record<string, unknown>
}

/**
 * Removes PII from a form-metadata schema before it is persisted.
 *
 * Operates at the `{ schema: { properties } }` level so it works for both note `meta_data` and
 * assignment/content metadata (where `schema` sits at the top). Always returns a new object and
 * never mutates the input; the input is returned as-is only when it has no schema properties.
 */
export const scrubSchemaPropertiesPII = <T extends { schema?: { properties?: Record<string, any> } }>(
  formMetadata: T,
  rules: PiiScrubRule[],
): T => {
  const properties = formMetadata.schema?.properties
  if (!properties) return formMetadata

  const nextProps: Record<string, any> = { ...properties }

  for (const { field, remove = [], reset = {} } of rules) {
    const prop = nextProps[field]
    if (!prop) continue

    const nextProp = { ...prop }
    for (const subkey of remove) {
      delete nextProp[subkey]
    }
    for (const [subkey, value] of Object.entries(reset)) {
      nextProp[subkey] = value
    }
    nextProps[field] = nextProp
  }

  return { ...formMetadata, schema: { ...formMetadata.schema, properties: nextProps } }
}
